import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  doc, onSnapshot, collection, collectionGroup, query,
  where, getDocs, setDoc, serverTimestamp
} from 'firebase/firestore'
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendPasswordResetEmail, updateProfile
} from 'firebase/auth'
import { db, auth } from '../firebase/config'
import {
  User, Award, Heart, Music, TrendingUp, Calendar, Clock,
  Flame, LogOut, Mail, Lock, Eye, EyeOff, KeyRound,
  Star, CreditCard, CheckCircle, XCircle, AlertCircle,
  ArrowLeft, LogIn, UserPlus
} from 'lucide-react'
import { toast } from '../hooks/useToast'
import './RadioPageTV.css'
import './ProfilPageTV.css'



// ── Badge statut adhésion ────────────────────────────────
function SubStatusBadge({ status }) {
  const cfg = {
    active:    { color:'#34d399', bg:'rgba(52,211,153,0.12)',  border:'rgba(52,211,153,0.35)',  icon:<CheckCircle size="1em"/>, label:'Active'     },
    completed: { color:'#34d399', bg:'rgba(52,211,153,0.12)',  border:'rgba(52,211,153,0.35)',  icon:<CheckCircle size="1em"/>, label:'Actif'      },
    pending:   { color:'#ffd700', bg:'rgba(255,215,0,0.12)',   border:'rgba(255,215,0,0.35)',   icon:<AlertCircle size="1em"/>, label:'En attente' },
    expired:   { color:'#ff6b6b', bg:'rgba(220,20,60,0.12)',   border:'rgba(220,20,60,0.35)',   icon:<XCircle     size="1em"/>, label:'Expirée'    },
  }
  const c = cfg[status] || cfg.pending
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:'0.45em', whiteSpace:'nowrap',
      padding:'0.35em 0.9em', borderRadius:999, fontSize:'0.85em', fontWeight:700,
      color:c.color, background:c.bg, border:`1px solid ${c.border}`,
    }}>
      {c.icon}{c.label}
    </span>
  )
}

// ── Helpers ──────────────────────────────────────────────
const fmtDate = (val) => {
  if (!val) return '—'
  const d = val.toDate?.() || new Date(val)
  return d.toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })
}

// ════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL — ProfilePage TV
// ════════════════════════════════════════════════════════
function ProfilePageTV({ currentUser, onLogout }) {
  const navigate = useNavigate()
  const tabRefs = useRef([])
  const emailRef = useRef(null)
  const passwordRef = useRef(null)
  const confirmRef = useRef(null)

  // ── Auth ─────────────────────────────────────────────
  const [authMode, setAuthMode]               = useState('login')
  const [email, setEmail]                     = useState('')
  const [password, setPassword]               = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName]         = useState('')
  const [showPassword, setShowPassword]       = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  // ── Profil ───────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('stats')
  const [userProfile, setUserProfile] = useState({
    totalListenTime:0, badges:[], streak:0, sessionsCount:0, recentTops:[]
  })
  const [userFavorites, setUserFavorites]       = useState([])
  const [loadingFavorites, setLoadingFavorites] = useState(true)

  // ── Adhésion ─────────────────────────────────────────
  const [adhesion, setAdhesion]         = useState(null)
  const [allAdhesions, setAllAdhesions] = useState([])
  const [loadingAdhesion, setLoadingAdhesion] = useState(true)

  const tabs = [
    { id:'stats',  icon:<TrendingUp />, label:'Statistiques' },
    { id:'badges', icon:<Award      />, label:'Badges'       },
    { id:'tops',   icon:<Music      />, label:'Mes TOPs'     },
    { id:'favs',   icon:<Heart      />, label:'Favoris'      },
    { id:'compte', icon:<CreditCard />, label:'Adhésion'     },
  ]

  // ── Télécommande ──────────────────────────────────────
  // Les flèches et OK sont gérés pour toute l'app par hooks/useTVRemote.js
  // (comme Paramètres et Classement). Ici : seulement la touche Retour.
  // Dans un champ de saisie, Retour/Effacer garde son rôle normal.
  useEffect(() => {
    const onKeyDown = (e) => {
      const el = document.activeElement
      if (el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return
      if (['Escape', 'BrowserBack', 'GoBack', 'Backspace'].includes(e.key) || e.keyCode === 10009) {
        e.preventDefault()
        navigate('/')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate])

  // ── Auth email ───────────────────────────────────────
  const handleAuth = async (e) => {
    e?.preventDefault?.()
    if (isAuthenticating) return
    // Validation manuelle : sur TV, pas de <form> (OK sur un champ
    // ouvrait la validation du navigateur au lieu du clavier).
    if (!email.trim() || !email.includes('@')) { toast.error('❌ Saisissez votre email'); emailRef.current?.focus(); return }
    if (authMode !== 'reset' && password.length < 6) { toast.error('❌ Mot de passe : 6 caractères minimum'); passwordRef.current?.focus(); return }
    setIsAuthenticating(true)
    try {
      if (authMode === 'reset') {
        await sendPasswordResetEmail(auth, email)
        toast.success('📧 Email envoyé !')
        setAuthMode('login'); setEmail(''); return
      }
      if (authMode === 'signup') {
        if (password !== confirmPassword) { toast.error('❌ Mots de passe différents'); return }
        if (password.length < 6)         { toast.error('❌ Minimum 6 caractères'); return }
        const cred = await createUserWithEmailAndPassword(auth, email, password)
        const name = displayName.trim() || email.split('@')[0]
        await updateProfile(cred.user, { displayName:name })
        await setDoc(doc(db,'users',cred.user.uid), {
          uid:cred.user.uid, email, displayName:name,
          totalListenTime:0, badges:[], streak:1, sessionsCount:0, recentTops:[],
          createdAt:serverTimestamp(), lastVisit:serverTimestamp()
        })
        toast.success('✅ Compte créé !')
        setEmail(''); setPassword(''); setConfirmPassword(''); setDisplayName(''); return
      }
      await signInWithEmailAndPassword(auth, email, password)
      toast.success('✅ Connexion réussie !')
      setEmail(''); setPassword('')
    } catch(error) {
      const msgs = {
        'auth/user-not-found':      '❌ Email introuvable',
        'auth/wrong-password':      '❌ Mot de passe incorrect',
        'auth/invalid-credential':  '❌ Email ou mot de passe incorrect',
        'auth/email-already-in-use':'❌ Email déjà utilisé',
        'auth/weak-password':       '❌ Mot de passe trop faible',
        'auth/too-many-requests':   '❌ Trop de tentatives',
      }
      toast.error(msgs[error.code] || '❌ Erreur : ' + error.code)
    } finally { setIsAuthenticating(false) }
  }

  // ── Profil Firestore ─────────────────────────────────
  // Le vrai historique de temps d'écoute vit parfois sous
  // totalListeningTime / totalSecondsWatched (écrits par un autre
  // système) plutôt que totalListenTime — on prend le maximum,
  // comme dans ClassementPageTV.jsx.
  useEffect(() => {
    if (!currentUser) return
    return onSnapshot(doc(db,'users',currentUser.uid), snap => {
      if (!snap.exists()) return
      const d = snap.data()
      const totalListenTime = Math.max(
        Number(d.totalListenTime || 0),
        Number(d.totalListeningTime || 0),
        Number(d.totalSecondsWatched || 0),
        Number(d.stats?.totalSecondsWatched || 0)
      )
      setUserProfile({
        totalListenTime,
        badges:          d.badges          || [],
        streak:          d.streak || d.currentStreak || d.stats?.currentStreak || 0,
        sessionsCount:   d.sessionsCount || d.sessions || d.stats?.sessionsCount || 0,
        recentTops:      d.recentTops      || [],
      })
    })
  }, [currentUser])

  // ── Favoris ──────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return
    ;(async () => {
      setLoadingFavorites(true)
      try {
        const snap = await getDocs(query(
          collection(db, '100johnny', 'main', 'user_song_votes'),
          where('userId','==',currentUser.uid),
          where('vote','==','top')
        ))
        setUserFavorites(
          snap.docs.map(d => ({ id:d.id, ...d.data() }))
            .sort((a,b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0))
        )
      } catch(e) { console.error(e) }
      finally { setLoadingFavorites(false) }
    })()
  }, [currentUser])

  // ── Adhésions ─────────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.uid) return
    ;(async () => {
      setLoadingAdhesion(true)
      let results = []

      // Tentative 1 : accès direct
      try {
        const ASSO_ID = '2ir61kP6b6SdKPbWM4UR'
        const snap = await getDocs(collection(db, '100johnny', 'main', 'associations', ASSO_ID, 'adhesions'))
        results = snap.docs
          .map(d => ({ id:d.id, ...d.data() }))
          .filter(d => d.userId === currentUser.uid)
      } catch(e) { console.warn('Direct:', e.message) }

      // Tentative 2 : collectionGroup userId
      if (results.length === 0) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db,'adhesions'), where('userId','==',currentUser.uid))
          )
          results = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        } catch(e) { console.warn('CollectionGroup:', e.message) }
      }

      // Tentative 3 : collectionGroup email
      if (results.length === 0) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db,'adhesions'), where('userEmail','==',currentUser.email))
          )
          results = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        } catch(e) { console.warn('Email:', e.message) }
      }

      results.sort((a,b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
      setAllAdhesions(results)
      setAdhesion(results.find(r => r.status === 'active') || null)
      setLoadingAdhesion(false)
    })()
  }, [currentUser?.uid])

  const formatTime = (s) => {
    const h = Math.floor(s/3600), m = Math.floor((s%3600)/60)
    return h > 0 ? `${h}h ${m}min` : `${m} min`
  }

  const allBadges = [
    { id:'first_listen',  name:'Première Écoute',  icon:'🎵', description:'Votre première chanson' },
    { id:'hour_listener', name:"1h d'Écoute",       icon:'⏰', description:"1 heure d'écoute"       },
    { id:'day_listener',  name:'Fan Quotidien',     icon:'📅', description:'24h cumulées'           },
    { id:'week_listener', name:'Mélomane',           icon:'🎸', description:"7 jours d'écoute"      },
    { id:'voter',         name:'Premier Vote',      icon:'👍', description:'Votre premier vote'     },
    { id:'top_voter',     name:'Critique Musical',  icon:'⭐', description:'10 votes'               },
    { id:'streak_7',      name:'Série de 7',        icon:'🔥', description:'7 jours consécutifs'   },
    { id:'night_owl',     name:'Oiseau de Nuit',    icon:'🦉', description:'Écoute après minuit'   },
  ]

  // ── FORMULAIRE AUTH (non connecté) ───────────────────
  // Même style que l'accueil TV (.tvh). Chaque élément est un vrai
  // bouton/champ empilé verticalement : ↑ ↓ passent de l'un à l'autre,
  // OK sur un champ ouvre le clavier de la TV.
  if (!currentUser) {
    const title = authMode === 'login' ? 'Connexion' : authMode === 'signup' ? 'Créer un compte' : 'Mot de passe oublié'
    const subtitle = authMode === 'login'
      ? 'Retrouvez vos statistiques, badges et favoris'
      : authMode === 'signup' ? 'Rejoignez la communauté 100% Johnny' : 'Recevez un lien de réinitialisation par email'

    // Touche « OK/Envoyer » du clavier TV sur le dernier champ : connexion
    // directe, seulement si tout est rempli. On ne bloque jamais la touche
    // (OK sur un champ vide doit ouvrir le clavier de la TV).
    const submitOnEnter = (e) => {
      if (e.key === 'Enter' && email.includes('@') && password.length >= 6) handleAuth()
    }

    const switchMode = (mode) => {
      setAuthMode(mode); setPassword(''); setConfirmPassword('')
      requestAnimationFrame(() => emailRef.current?.focus())
    }

    return (
      <div className="tvh tvl">
        <header className="tvl-head">
          <button type="button" className="tvh-btn" onClick={() => navigate('/')}>
            <ArrowLeft size="1.1em" /> Accueil
          </button>
          <div className="tvl-brand">
            <img src="/images/logo.png" alt="" className="tvh-logo" />
            <span>100% Johnny</span>
          </div>
        </header>

        <main className="tvl-main">
          <section className="tvl-intro">
            <span className="tvl-intro__icon">
              {authMode === 'reset' ? <KeyRound /> : authMode === 'signup' ? <UserPlus /> : <User />}
            </span>
            <h1>{title}</h1>
            <p>{subtitle}</p>
            <ul className="tvl-tips">
              <li><span className="tvh-key">▲</span><span className="tvh-key">▼</span> Passer d'un champ à l'autre</li>
              <li><span className="tvh-key tvh-key--wide">OK</span> Ouvrir le clavier / valider</li>
              <li><span className="tvh-key tvh-key--wide">↩</span> Fermer le clavier</li>
            </ul>
          </section>

          <section className={`tvl-card ${authMode === 'signup' ? 'tvl-card--compact' : ''}`} aria-label={title}>
            {authMode === 'signup' && (
              <label className="tvl-field">
                <span>Pseudo (facultatif)</span>
                <span className="tvl-input">
                  <User />
                  <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                    placeholder="Votre nom de fan" enterKeyHint="next" />
                </span>
              </label>
            )}

            <label className="tvl-field">
              <span>Email</span>
              <span className="tvl-input">
                <Mail />
                <input ref={emailRef} type="email" inputMode="email" autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="votre@email.com" data-tv-initial-focus=""
                  enterKeyHint={authMode === 'reset' ? 'done' : 'next'} />
              </span>
            </label>

            {authMode !== 'reset' && (
              <label className="tvl-field">
                <span>Mot de passe</span>
                <span className="tvl-input">
                  <Lock />
                  <input ref={passwordRef} type={showPassword ? 'text' : 'password'}
                    autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                    value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                    enterKeyHint={authMode === 'signup' ? 'next' : 'go'}
                    onKeyDown={authMode === 'login' ? submitOnEnter : undefined} />
                </span>
              </label>
            )}

            {authMode === 'signup' && (
              <label className="tvl-field">
                <span>Confirmer le mot de passe</span>
                <span className="tvl-input">
                  <Lock />
                  <input ref={confirmRef} type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••"
                    enterKeyHint="go" onKeyDown={submitOnEnter} />
                </span>
              </label>
            )}

            {authMode !== 'reset' && (
              <div className="tvl-row">
                <button type="button" className="tvh-btn tvl-small" onClick={() => setShowPassword(v => !v)}>
                  {showPassword ? <EyeOff /> : <Eye />} {showPassword ? 'Masquer' : 'Afficher'} le mot de passe
                </button>
                {authMode === 'login' && (
                  <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('reset')}>
                    <KeyRound /> Mot de passe oublié ?
                  </button>
                )}
              </div>
            )}

            <button type="button" className="tvl-submit" onClick={handleAuth} disabled={isAuthenticating}>
              {isAuthenticating ? 'Un instant…'
                : authMode === 'login' ? <><LogIn /> Se connecter</>
                : authMode === 'signup' ? <><UserPlus /> Créer mon compte</>
                : <><Mail /> Envoyer le lien</>}
            </button>

            <div className="tvl-switch">
              {authMode === 'login' && (<>
                <span>Pas encore de compte ?</span>
                <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('signup')}>
                  <UserPlus /> Créer un compte
                </button>
              </>)}
              {authMode === 'signup' && (<>
                <span>Déjà inscrit ?</span>
                <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('login')}>
                  <LogIn /> Se connecter
                </button>
              </>)}
              {authMode === 'reset' && (
                <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('login')}>
                  <ArrowLeft /> Retour à la connexion
                </button>
              )}
            </div>
          </section>
        </main>
      </div>
    )
  }

  // ── PROFIL CONNECTÉ ──────────────────────────────────
  // Même style que l'accueil, les Paramètres et le Classement TV.
  // Un seul écran : onglets à gauche (↑ ↓), contenu à droite.
  const name = currentUser.displayName || currentUser.email?.split('@')[0]
  const MAX_LIST = 8
  const unlockedCount = allBadges.filter(b => userProfile.badges.includes(b.id)).length
  const mainAdhesion = adhesion || allAdhesions[0] || null
  const otherAdhesions = allAdhesions.length - (mainAdhesion ? 1 : 0)

  const SongList = ({ items, getTitle, getArtist, ranked }) => (
    <>
      <div className="tvp-songs">
        {items.slice(0, MAX_LIST).map((it, i) => (
          <div key={it.id || i} className="tvp-song">
            {ranked && <span className="tvp-song__rank">{i + 1}</span>}
            <span className="tvp-song__cover">
              {it.cover ? <img src={it.cover} alt="" /> : <Music />}
            </span>
            <span className="tvp-song__body">
              <strong>{getTitle(it)}</strong>
              <span>{getArtist(it)}</span>
            </span>
            <Heart className="tvp-song__heart" fill="currentColor" />
          </div>
        ))}
      </div>
      {items.length > MAX_LIST && (
        <div className="tvp-more">+ {items.length - MAX_LIST} autre{items.length - MAX_LIST > 1 ? 's' : ''} titre{items.length - MAX_LIST > 1 ? 's' : ''}</div>
      )}
    </>
  )

  const Empty = ({ icon: Icon, title, text, children }) => (
    <div className="tvp-empty">
      <Icon />
      <strong>{title}</strong>
      <span>{text}</span>
      {children}
    </div>
  )

  const renderAdhesion = () => {
    if (loadingAdhesion) {
      return <div className="tvp-empty"><div className="tvp-spinner" />Chargement de votre adhésion…</div>
    }
    if (!mainAdhesion) {
      return (
        <Empty icon={CreditCard} title="Vous n'êtes pas encore membre" text="Rejoignez l'association pour soutenir la légende de Johnny">
          <button type="button" className="tvp-cta" onClick={() => navigate('/abonnement')}>
            <CreditCard size="1.1em" /> Adhérer — 20&nbsp;€/an
          </button>
        </Empty>
      )
    }
    const sub = mainAdhesion
    const endDateObj = sub.endDate ? (sub.endDate.toDate?.() || new Date(sub.endDate)) : null
    const isExpired  = endDateObj ? endDateObj < new Date() : false
    const daysLeft   = endDateObj ? Math.ceil((endDateObj - new Date()) / 86400000) : null
    let pct = null
    if (sub.startDate && sub.endDate) {
      const start = (sub.startDate.toDate?.() || new Date(sub.startDate)).getTime()
      const end   = (sub.endDate.toDate?.()   || new Date(sub.endDate)).getTime()
      pct = Math.min(100, Math.max(0, Math.round(((Date.now() - start) / (end - start)) * 100)))
    }
    const infos = [
      { label: 'N° membre', value: 'JH·' + sub.id.substring(0, 8).toUpperCase(), mono: true },
      { label: 'Montant',   value: sub.totalPrice || sub.subscriptionPrice ? `${sub.totalPrice || sub.subscriptionPrice} €` : '—' },
      { label: 'Paiement',  value: sub.paymentMethod === 'manual' ? 'Manuel' : sub.paymentMethod === 'paypal' ? 'PayPal' : (sub.paymentMethod || '—') },
      { label: 'Début',     value: fmtDate(sub.startDate) },
      { label: 'Expire le', value: fmtDate(sub.endDate), warn: isExpired },
      { label: 'Créée le',  value: fmtDate(sub.createdAt) }
    ]
    return (
      <div className="tvp-adh">
        <div className="tvp-adh__head">
          <div>
            <strong>Adhésion 100% Johnny</strong>
            <span>Association Radio 100% Johnny · {sub.type === 'annuelle' ? 'Annuelle' : (sub.type || 'Annuelle')}</span>
          </div>
          <SubStatusBadge status={isExpired ? 'expired' : (sub.status || 'pending')} />
        </div>

        <div className="tvp-adh__grid">
          {infos.map(({ label, value, mono, warn }) => (
            <div key={label} className="tvp-adh__info">
              <span>{label}</span>
              <strong className={`${mono ? 'is-mono' : ''} ${warn ? 'is-warn' : ''}`}>{value}</strong>
            </div>
          ))}
        </div>

        {pct !== null && (
          <div className="tvp-adh__progress">
            <div className="tvp-adh__progress-row">
              <span>Validité écoulée</span>
              <strong className={daysLeft !== null && daysLeft < 30 ? 'is-warn' : 'is-ok'}>
                {daysLeft !== null ? (daysLeft > 0 ? `${daysLeft} jours restants` : 'Expiré') : '—'}
              </strong>
            </div>
            <div className="tvp-bar"><i style={{ transform: `scaleX(${pct / 100})` }} /></div>
          </div>
        )}

        {daysLeft !== null && daysLeft <= 30 && (
          <div className={`tvp-alert ${isExpired ? 'is-warn' : ''}`}>
            {isExpired
              ? 'Adhésion expirée — Renouvelez sur 100pour100johnny.com'
              : `Expire dans ${daysLeft} jours — Renouvelez sur 100pour100johnny.com`}
          </div>
        )}

        {otherAdhesions > 0 && (
          <div className="tvp-more">+ {otherAdhesions} autre{otherAdhesions > 1 ? 's' : ''} adhésion{otherAdhesions > 1 ? 's' : ''} (historique visible sur le téléphone ou le site)</div>
        )}
      </div>
    )
  }

  const panelTitle = {
    stats:  ['Statistiques', "Votre activité d'auditeur"],
    badges: ['Badges', `${unlockedCount} / ${allBadges.length} débloqués`],
    tops:   ['Mes TOPs', 'Les titres que vous avez aimés récemment'],
    favs:   ['Favoris', 'Vos chansons votées TOP'],
    compte: ['Adhésion', 'Association Radio 100% Johnny']
  }[activeTab]

  return (
    <div className="tvh tvp">
      {/* EN-TÊTE */}
      <header className="tvp-head">
        <button type="button" className="tvh-btn" onClick={() => navigate('/')}>
          <ArrowLeft size="1.1em" /> Accueil
        </button>

        <div className="tvp-user">
          <span className="tvp-avatar">
            {currentUser.photoURL ? <img src={currentUser.photoURL} alt="" /> : <User />}
          </span>
          <div className="tvp-user__text">
            <h1>{name}</h1>
            <p>
              {currentUser.email}
              {adhesion && <span className="tvp-member"><Star size="0.9em" fill="currentColor" /> Membre association</span>}
            </p>
          </div>
        </div>

        <button type="button" className="tvh-btn is-alert" onClick={onLogout}>
          <LogOut size="1.1em" /> Déconnexion
        </button>
      </header>

      <div className="tvp-main">
        {/* ONGLETS */}
        <nav className="tvp-tabs" aria-label="Rubriques du profil">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              type="button"
              ref={el => { tabRefs.current[index] = el }}
              className={`tvh-menu__item tvp-tab ${activeTab === tab.id ? 'is-on' : ''}`}
              aria-pressed={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              onFocus={() => setActiveTab(tab.id)}
              data-tv-initial-focus={index === 0 ? '' : undefined}
            >
              <span className="tvh-menu__icon">{tab.icon}</span>
              {tab.label}
              {tab.id === 'compte' && adhesion && <span className="tvp-tab__dot" aria-label="Adhésion active" />}
            </button>
          ))}
        </nav>

        {/* CONTENU */}
        <section className="tvp-panel">
          <div className="tvp-panel__head">
            <h2>{panelTitle[0]}</h2>
            <span>{panelTitle[1]}</span>
          </div>

          <div className="tvp-panel__body">
            {activeTab === 'stats' && (
              <div className="tvp-stats">
                {[
                  { icon: Clock,    value: formatTime(userProfile.totalListenTime), label: "Temps d'écoute" },
                  { icon: Flame,    value: userProfile.streak,                      label: 'Jours de suite' },
                  { icon: Calendar, value: userProfile.sessionsCount,               label: 'Sessions' },
                  { icon: Award,    value: userProfile.badges.length,               label: 'Badges' }
                ].map(({ icon: Icon, value, label }) => (
                  <div key={label} className="tvp-stat">
                    <span className="tvp-stat__icon"><Icon /></span>
                    <div>
                      <strong>{value}</strong>
                      <span>{label}</span>
                    </div>
                  </div>
                ))}
                {!loadingAdhesion && adhesion && (
                  <div className="tvp-stat tvp-stat--wide">
                    <span className="tvp-tab__dot" />
                    <div>
                      <strong className="tvp-ok">Adhésion active</strong>
                      <span>Votre carte membre est dans la rubrique Adhésion.</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'badges' && (
              <div className="tvp-badges">
                {allBadges.map(badge => {
                  const unlocked = userProfile.badges.includes(badge.id)
                  return (
                    <div key={badge.id} className={`tvp-badge ${unlocked ? 'is-on' : ''}`}>
                      <span className="tvp-badge__emoji">{badge.icon}</span>
                      <strong>{badge.name}</strong>
                      <span>{badge.description}</span>
                      {!unlocked && <Lock className="tvp-badge__lock" />}
                    </div>
                  )
                })}
              </div>
            )}

            {activeTab === 'tops' && (
              userProfile.recentTops?.length > 0
                ? <SongList items={userProfile.recentTops} getTitle={t => t.title} getArtist={t => t.artist} ranked />
                : <Empty icon={Music} title="Aucun TOP enregistré" text="Votez pour vos chansons préférées !" />
            )}

            {activeTab === 'favs' && (
              loadingFavorites
                ? <div className="tvp-empty"><div className="tvp-spinner" />Chargement…</div>
                : userFavorites.length > 0
                  ? <SongList items={userFavorites} getTitle={f => f.songTitle} getArtist={f => f.songArtist} />
                  : <Empty icon={Heart} title="Aucun favori" text="Votez TOP pour vos chansons favorites !" />
            )}

            {activeTab === 'compte' && renderAdhesion()}
          </div>
        </section>
      </div>

      <footer className="tvp-foot">
        <span>▲ ▼ Changer de rubrique · OK Choisir</span>
        <span>↩ Retour : revenir à l'accueil</span>
      </footer>
    </div>
  )
}

export default ProfilePageTV